import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { GlobalVariables } from '../../../global-variables';
import { Observable } from 'rxjs';

export type SubscriptionBillingCycle = 'monthly' | 'annual';

export interface RazorpayOrderRequest {
  amount: number;
  currency?: string;
  receipt?: string;
  details?: {
    id: string;
    type: 'subscription';
    duration: SubscriptionBillingCycle;
    userId?: string;
    [key: string]: any;
  };
}

export interface RazorpayVerifyRequest {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

export interface StripeVerifyRequest {
  paymentIntentId: string;
}

export interface StripePaymentIntentRequest {
  amount: number;
  currency?: string;
  receipt?: string;
  details?: {
    id: string;
    type: 'subscription';
    duration: SubscriptionBillingCycle;
    userId?: string;
    planName?: string;
    [key: string]: any;
  };
}

export interface SubscribePlanRequest {
  userId?: string;
  duration?: number;
  vcardCount?: number;
  storeCount?: number;
  featuredProductCount?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private url = GlobalVariables.appURL;

  constructor(private _httpClient: HttpClient) { }

  public list(country?: string): Observable<any> {
    const options: { params?: HttpParams } = {};
    if (country) {
      options.params = new HttpParams().set('country', country);
    }
    return this._httpClient.get(this.url + 'subscription-plans', options);
  }

  public detail(id: any): Observable<any> {
    return this._httpClient.get(this.url + 'subscription-plans/' + id);
  }

  public delete(id: any): Observable<any> {
    return this._httpClient.delete(this.url + 'subscription-plans/' + id);
  }

  public create(data: any): Observable<any> {
    return this._httpClient.post(this.url + 'subscription-plans', { ...data, duration: 30 });
  }

  public update(data: any, id: string): Observable<any> {
    return this._httpClient.patch(this.url + 'subscription-plans/' + id, { ...data, duration: 30 });
  }

  public approve(id: any): Observable<any> {
    return this._httpClient.patch(this.url + 'subscription-plans/' + id + '/approve', {});
  }

  public getSubscriptionPlanMenu(id: string): Observable<any> {
    return this._httpClient.get(this.url + 'subscription-plans/' + id + '/menu');
  }

  public updateSubscriptionPlanMenu(id: string, data: any[]): Observable<any> {
    return this._httpClient.patch(this.url + 'subscription-plans/' + id + '/menu', data);
  }

  public subscribeToPlan(planId: string, data: SubscribePlanRequest): Observable<any> {
    return this._httpClient.post(this.url + 'subscription-plans/' + planId + '/subscribe', data);
  }

  public createRazorpayOrder(data: RazorpayOrderRequest): Observable<any> {
    return this._httpClient.post(this.url + 'payments/razorpay/order', data);
  }

  public verifyRazorpayPayment(data: RazorpayVerifyRequest): Observable<any> {
    return this._httpClient.post(this.url + 'payments/razorpay/verify', data);
  }

  public createStripePaymentIntent(data: StripePaymentIntentRequest): Observable<any> {
    return this._httpClient.post(this.url + 'payments/stripe/payment-intent', data);
  }

  public verifyStripePayment(data: StripeVerifyRequest): Observable<any> {
    return this._httpClient.post(this.url + 'payments/stripe/verify', data);
  }
}
